<template>
  <modal-wrap
    :id="modalName"
    :name="modalName"
    :hide-footer="true"
    header-class="border-0 fix-error-header"
    body-class="integrations-modal"
    size="md"
  >
    <template #modal-title>
      <h4 class="mb-0">Update Status</h4>
    </template>
    <template #default>
      <select v-model="status" class="form-control">
        <option v-for="option in options" :key="option.value" :value="option.value">
          {{ option.text }}
        </option>
      </select>
      <div class="mt-3">
        <button class="btn btn-green" @click="updateStatus">Update Status</button>
      </div>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { SelectOptions } from '@/types/Components/SelectOptions'
import type { Assignment } from '@/types/Models/Assignment'
import { defineComponent, ref, watch, computed, type ComputedRef, type Ref } from 'vue'
import {
  CANCELLED,
  COMPLETE,
  LABELS_CREATING,
  LABELS_RELEASED,
  OPEN,
  OPTIMISING,
  PENDING,
  REPORT_ACCEPTED,
  REPORT_DENIED,
  REPORT_RELEASED,
  VALIDATING,
  VALIDATION_COMPLETE,
} from '@/const/AssignmentStatuses'
import { ASSIGNMENT_STATUS_MODAL } from '@/const/ModalTypes'
import { registerModals } from '@/services/Modal'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'

export default defineComponent({
  name: 'StatusManagementModal',
  components: { ModalWrap },
  setup() {
    const modalName = ASSIGNMENT_STATUS_MODAL
    const modalStore = useVueNextModalStore()
    const assignmentSummaryStore = useAssignmentSummaryStore()

    const assignmentRef: ComputedRef<string | null> = computed(
      () => modalStore.modalPropsByName(modalName)?.assignmentRef || null,
    )
    const assignment: ComputedRef<Assignment | null> = computed(() => assignmentSummaryStore.assignment)
    const status: Ref<string> = ref('')
    const options: SelectOptions[] = [
      { value: '', text: 'Status' },
      { value: OPEN, text: 'Open' },
      { value: PENDING, text: 'Pending' },
      { value: VALIDATING, text: 'Validating' },
      { value: VALIDATION_COMPLETE, text: 'Validation Complete' },
      { value: OPTIMISING, text: 'Optimising' },
      { value: REPORT_RELEASED, text: 'Report Released' },
      { value: REPORT_ACCEPTED, text: 'Report Accepted' },
      { value: REPORT_DENIED, text: 'Report Rejected' },
      { value: LABELS_CREATING, text: 'Labels Creating' },
      { value: LABELS_RELEASED, text: 'Labels Released' },
      { value: CANCELLED, text: 'Cancelled' },
      { value: COMPLETE, text: 'Complete' },
    ]

    const updateStatus = () => {
      if (assignment.value && status.value && assignmentRef.value) {
        assignmentSummaryStore.updateStatus({
          assignmentId: assignmentRef.value,
          newStatus: status.value,
        })
      }
    }

    registerModals(modalName)

    watch(
      assignment,
      () => {
        status.value = assignment.value?.status || ''
      },
      { immediate: true, deep: true },
    )

    return {
      status,
      options,
      modalName,
      assignment,
      assignmentRef,
      updateStatus,
    }
  },
})
</script>
